
import React, { useState } from 'react';
import { View, Text, TextInput, ScrollView, StyleSheet, TouchableOpacity, Alert, SafeAreaView, Modal } from 'react-native';
import SvgIcon from '../components/SvgIcon';
import Toast from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import colors from '../theme/colors';
import { useToast, checkNetworkError } from '../hooks/useToast';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const COUNTRIES = ['India', 'USA', 'UK', 'Australia', 'Canada'];
const COUNTRY_CODES = ['91', '1', '44'];

const AddProfileScreen = ({ navigation, route }) => {
  const { user } = useAuth();
  const { toast, showToast } = useToast();
  const initialGender = route?.params?.initialGender || 'Female';
  const initialMemberType = initialGender === 'Female' ? 'Bride' : 'Groom';
  
  const [form, setForm] = useState({
    name: '',
    surName: '',
    dateOfBirth: '',
    fatherName: '',
    motherName: '',
    profession: '',
    gender: initialGender,
    memberType: initialMemberType,
    address: '',
    area: '',
    city: '',
    state: '',
    country: 'India',
    pinCode: '',
    countryCode: '91',
    mobileNumber: '',
    email: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showMemberTypeModal, setShowMemberTypeModal] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showCountryCodeModal, setShowCountryCodeModal] = useState(false);
  const [dialog, setDialog] = useState({ visible: false, type: '', title: '', message: '', onConfirm: null });
  const [mobileNumberFocused, setMobileNumberFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);
  const [surNameFocused, setSurNameFocused] = useState(false);
  const [dateOfBirthFocused, setDateOfBirthFocused] = useState(false);
  const [professionFocused, setProfessionFocused] = useState(false);
  const [fatherNameFocused, setFatherNameFocused] = useState(false);
  const [motherNameFocused, setMotherNameFocused] = useState(false);
  const [addressFocused, setAddressFocused] = useState(false);
  const [areaFocused, setAreaFocused] = useState(false);
  const [cityFocused, setCityFocused] = useState(false);
  const [pinCodeFocused, setPinCodeFocused] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);

  const handleChange = (key, value) => {
    // Auto-update gender when memberType changes
    if (key === 'memberType') {
      setForm({ 
        ...form, 
        [key]: value,
        gender: value === 'Bride' ? 'Female' : 'Male'
      });
    } else {
      setForm({ ...form, [key]: value });
    }
  };

  const showDialog = (type, title, message, onConfirm = null) => {
    setDialog({ visible: true, type, title, message, onConfirm });
  };

  const hideDialog = () => {
    setDialog({ visible: false, type: '', title: '', message: '', onConfirm: null });
  };

  const handleDialogConfirm = () => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
    hideDialog();
  };

  const handleClear = () => {
    setForm({
      name: '',
      surName: '',
      dateOfBirth: '',
      fatherName: '',
      motherName: '',
      profession: '',
      gender: 'Female',
      memberType: 'Bride',
      address: '',
      area: '',
      city: '',
      state: '',
      country: 'India',
      pinCode: '',
      countryCode: '91',
      mobileNumber: '',
      email: '',
    });
  };

  const validateForm = () => {
    // Required fields
    const requiredFields = [
      { key: 'name', label: 'First Name' },
      { key: 'surName', label: 'Last Name' },
      { key: 'dateOfBirth', label: 'Date of Birth' },
      { key: 'profession', label: 'Profession' },
      { key: 'memberType', label: 'Profile Type' },
      { key: 'address', label: 'Address' },
      { key: 'area', label: 'Area' },
      { key: 'city', label: 'City/Town/Village' },
      { key: 'state', label: 'State' },
      { key: 'country', label: 'Country' },
      { key: 'pinCode', label: 'Postal Code' },
      { key: 'mobileNumber', label: 'Mobile Number' },
      { key: 'email', label: 'Email' },
    ];
    for (let field of requiredFields) {
      if (!form[field.key] || form[field.key].trim() === '') {
        showDialog('error', 'Validation Error', `${field.label} is required.`);
        return false;
      }
    }
    // Name validation (letters, spaces, hyphens, apostrophes only)
    const nameRegex = /^[a-zA-Z\s'-]+$/;
    if (!nameRegex.test(form.name)) {
      showDialog('error', 'Validation Error', 'First Name should contain only letters.');
      return false;
    }
    if (!nameRegex.test(form.surName)) {
      showDialog('error', 'Validation Error', 'Last Name should contain only letters.');
      return false;
    }

    // Name length validation
    if (form.name.trim().length < 2 || form.name.trim().length > 50) {
      showDialog('error', 'Validation Error', 'First Name must be between 2 and 50 characters.');
      return false;
    }
    if (form.surName.trim().length < 2 || form.surName.trim().length > 50) {
      showDialog('error', 'Validation Error', 'Last Name must be between 2 and 50 characters.');
      return false;
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(form.email)) {
      showDialog('error', 'Validation Error', 'Please enter a valid email address.');
      return false;
    }

    // Mobile number validation
    if (!/^\d{10}$/.test(form.mobileNumber)) {
      showDialog('error', 'Validation Error', 'Please enter a valid 10-digit mobile number.');
      return false;
    }

    // Date of Birth validation and age check
    if (!/^\d{2}-\d{2}-\d{4}$/.test(form.dateOfBirth)) {
      showDialog('error', 'Validation Error', 'Date of Birth must be in DD-MM-YYYY format.');
      return false;
    }
    
    // Parse DD-MM-YYYY format
    const [day, month, year] = form.dateOfBirth.split('-');
    const dob = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear() - 
                (today.getMonth() < dob.getMonth() || 
                 (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate()) ? 1 : 0);
    if (isNaN(dob.getTime())) {
      showDialog('error', 'Validation Error', 'Please enter a valid date of birth.');
      return false;
    }
    if (age < 18) {
      showDialog('error', 'Validation Error', 'Member must be at least 18 years old.');
      return false;
    }
    if (age > 100) {
      showDialog('error', 'Validation Error', 'Please enter a valid date of birth.');
      return false;
    }

    // Profession validation
    if (form.profession.trim().length < 2 || form.profession.trim().length > 100) {
      showDialog('error', 'Validation Error', 'Profession must be between 2 and 100 characters.');
      return false;
    }

    // Address validations
    if (form.address.trim().length < 5 || form.address.trim().length > 200) {
      showDialog('error', 'Validation Error', 'Address must be between 5 and 200 characters.');
      return false;
    }
    if (form.area.trim().length < 2 || form.area.trim().length > 100) {
      showDialog('error', 'Validation Error', 'Area must be between 2 and 100 characters.');
      return false;
    }
    if (form.city.trim().length < 2 || form.city.trim().length > 100) {
      showDialog('error', 'Validation Error', 'City must be between 2 and 100 characters.');
      return false;
    }

    // Pin code validation (6 digits)
    if (!/^\d{6}$/.test(form.pinCode)) {
      showDialog('error', 'Validation Error', 'Please enter a valid 6-digit postal code.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      // Convert DD-MM-YYYY to YYYY-MM-DD for API
      const [day, month, year] = form.dateOfBirth.split('-');
      const dobForApi = `${year}-${month}-${day}`;
      
      const payload = {
        ...form,
        dateOfBirth: dobForApi,
        mediatorId: user?.mediatorId,
        createdBy: user?.userId,
      };
      console.log('Submitting payload:', payload);
      const res = await apiClient.post('/api/Auth/register/member', payload);
      console.log('API Response:', res.data);
      
      const memberName = res?.data?.name && res?.data?.surName 
        ? `${res.data.name} ${res.data.surName}` 
        : res?.data?.name || 'New member';
      
      showDialog(
        'success',
        'Success',
        `Profile for ${memberName} created successfully!`,
        () => {
          console.log('Navigating to Profiles');
          navigation.navigate('Profiles');
        }
      );
    } catch (error) {
      console.error('Registration error:', error);
      if (checkNetworkError(error)) {
        showToast('No internet connection. Please check your network and try again.');
      } else {
        showDialog('error', 'Error', error?.response?.data?.message || error.message || 'Failed to create profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const CustomDropdown = ({ label, value, onPress, placeholder, disabled, style }) => (
    <TouchableOpacity 
      style={[styles.dropdown, disabled && styles.disabledDropdown, style]} 
      onPress={disabled ? null : onPress}
      disabled={disabled}
    >
      <Text style={value ? styles.dropdownText : styles.dropdownPlaceholder}>
        {value || placeholder}
      </Text>
      {!disabled && <SvgIcon name="chevron-down" size={20} color={colors.textSecondary} />}
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: '#FFFFFF' }}>
      <View style={styles.headerSection}>
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <SvgIcon name="arrow-back" size={24} color={colors.white} />
          </TouchableOpacity>
          <View style={styles.headerTextContainer}>
            <Text style={styles.title}>Add New Profile</Text>
            <Text style={styles.requiredNote}>* indicates required fields</Text>
          </View>
          <View style={styles.headerSpacer} />
        </View>
      </View>
      <ScrollView contentContainerStyle={styles.container}>
        {/* Profile Type */}
        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Profile Type *</Text>
          <CustomDropdown
            value={form.memberType === 'Bride' ? 'Bride (వధువు)' : 'Groom (వరుడు)'}
            placeholder="Select Profile Type"
            onPress={() => setShowMemberTypeModal(true)}
          />
        </View>

        {/* Personal Information Section */}
        <Text style={styles.sectionHeader}>Personal Information</Text>
        
        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>First Name *</Text>
          <TextInput 
            style={[styles.input, nameFocused && styles.inputFocused]} 
            placeholder="Enter First Name" 
            placeholderTextColor="#9CA3AF"
            value={form.name} 
            onChangeText={v => handleChange('name', v)} 
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Last Name *</Text>
          <TextInput 
            style={[styles.input, surNameFocused && styles.inputFocused]} 
            placeholder="Enter Sur Name/ Last Name" 
            placeholderTextColor="#9CA3AF"
            value={form.surName} 
            onChangeText={v => handleChange('surName', v)} 
            onFocus={() => setSurNameFocused(true)}
            onBlur={() => setSurNameFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Date of Birth *</Text>
          <TextInput 
            style={[styles.input, dateOfBirthFocused && styles.inputFocused]} 
            placeholder="dd-mm-yyyy" 
            placeholderTextColor="#9CA3AF"
            value={form.dateOfBirth} 
            onChangeText={v => handleChange('dateOfBirth', v)} 
            onFocus={() => setDateOfBirthFocused(true)}
            onBlur={() => setDateOfBirthFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Profession *</Text>
          <TextInput 
            style={[styles.input, professionFocused && styles.inputFocused]} 
            placeholder="Enter Profession" 
            placeholderTextColor="#9CA3AF"
            value={form.profession} 
            onChangeText={v => handleChange('profession', v)} 
            onFocus={() => setProfessionFocused(true)}
            onBlur={() => setProfessionFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Father's Name</Text>
          <TextInput 
            style={[styles.input, fatherNameFocused && styles.inputFocused]} 
            placeholder="Enter Father's Name" 
            placeholderTextColor="#9CA3AF"
            value={form.fatherName} 
            onChangeText={v => handleChange('fatherName', v)} 
            onFocus={() => setFatherNameFocused(true)}
            onBlur={() => setFatherNameFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Mother's Name</Text>
          <TextInput 
            style={[styles.input, motherNameFocused && styles.inputFocused]} 
            placeholder="Enter Mother's Name" 
            placeholderTextColor="#9CA3AF"
            value={form.motherName} 
            onChangeText={v => handleChange('motherName', v)} 
            onFocus={() => setMotherNameFocused(true)}
            onBlur={() => setMotherNameFocused(false)}
          />
        </View>

        {/* Present Address Section */}
        <Text style={styles.sectionHeader}>Present Address</Text>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Address *</Text>
          <TextInput 
            style={[styles.input, addressFocused && styles.inputFocused]} 
            placeholder="Enter Address (House No, Street, etc.)" 
            placeholderTextColor="#9CA3AF"
            value={form.address} 
            onChangeText={v => handleChange('address', v)} 
            onFocus={() => setAddressFocused(true)}
            onBlur={() => setAddressFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Area *</Text>
          <TextInput 
            style={[styles.input, areaFocused && styles.inputFocused]} 
            placeholder="Enter Area" 
            placeholderTextColor="#9CA3AF"
            value={form.area} 
            onChangeText={v => handleChange('area', v)} 
            onFocus={() => setAreaFocused(true)}
            onBlur={() => setAreaFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>City/Town/Village *</Text>
          <TextInput 
            style={[styles.input, cityFocused && styles.inputFocused]} 
            placeholder="Enter City/Town/Village" 
            placeholderTextColor="#9CA3AF"
            value={form.city} 
            onChangeText={v => handleChange('city', v)} 
            onFocus={() => setCityFocused(true)}
            onBlur={() => setCityFocused(false)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>State *</Text>
          <CustomDropdown
            value={form.state}
            placeholder="Select State"
            onPress={() => setShowStateModal(true)}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Country *</Text>
          <CustomDropdown
            value={form.country}
            placeholder="Select Country"
            onPress={() => {}}
            disabled={true}
            style={styles.disabledInput}
          />
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Postal Code *</Text>
          <TextInput 
            style={[styles.input, pinCodeFocused && styles.inputFocused]} 
            placeholder="Enter Postal Code" 
            placeholderTextColor="#9CA3AF"
            value={form.pinCode} 
            onChangeText={v => handleChange('pinCode', v)} 
            keyboardType="numeric" 
            maxLength={6}
            onFocus={() => setPinCodeFocused(true)}
            onBlur={() => setPinCodeFocused(false)}
          />
        </View>

        {/* Primary Contact Section */}
        <Text style={styles.sectionHeader}>Primary Contact</Text>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Mobile Number (Used for Login) *</Text>
          <View style={[styles.groupedInputContainer, mobileNumberFocused && styles.groupedInputContainerFocused]}>
            <View style={styles.countryCodeButtonGrouped}>
              <Text style={styles.countryCodeText}>{form.countryCode}</Text>
            </View>
            <TextInput 
              style={styles.phoneInputGrouped} 
              placeholder="Enter 10 digit Mobile Number" 
              placeholderTextColor="#9CA3AF"
              value={form.mobileNumber} 
              onChangeText={v => handleChange('mobileNumber', v)} 
              keyboardType="phone-pad" 
              maxLength={10}
              onFocus={() => setMobileNumberFocused(true)}
              onBlur={() => setMobileNumberFocused(false)}
            />
          </View>
        </View>

        <View style={styles.inputWrapper}>
          <Text style={styles.floatingLabel}>Email *</Text>
          <TextInput 
            style={[styles.input, emailFocused && styles.inputFocused]} 
            placeholder="Enter Email" 
            placeholderTextColor="#9CA3AF"
            value={form.email} 
            onChangeText={v => handleChange('email', v)} 
            keyboardType="email-address"
            autoCapitalize="none"
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
          />
        </View>

        {/* Buttons */}
        <View style={styles.buttonRow}>
          <TouchableOpacity 
            style={[styles.button, styles.registerButton]} 
            onPress={handleSubmit}
            disabled={isLoading}
          >
            <Text style={styles.buttonText}>{isLoading ? 'Submitting...' : 'Register'}</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.button, styles.clearButton]} 
            onPress={handleClear}
            disabled={isLoading}
          >
            <Text style={styles.buttonText}>Clear</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Member Type Modal */}
      <Modal visible={showMemberTypeModal} transparent animationType="slide">
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1} 
          onPress={() => setShowMemberTypeModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Profile Type</Text>
            {[
              { label: 'Bride (వధువు)', value: 'Bride' },
              { label: 'Groom (వరుడు)', value: 'Groom' }
            ].map((item) => (
              <TouchableOpacity
                key={item.value}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('memberType', item.value);
                  setShowMemberTypeModal(false);
                }}
              >
                <Text style={styles.modalItemText}>{item.label}</Text>
                {form.memberType === item.value && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* State Modal */}
      <Modal visible={showStateModal} transparent animationType="slide">
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1} 
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select State</Text>
            <ScrollView style={styles.modalScroll}>
              {INDIAN_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.modalItem}
                  onPress={() => {
                    handleChange('state', state);
                    setShowStateModal(false);
                  }}
                >
                  <Text style={styles.modalItemText}>{state}</Text>
                  {form.state === state && (
                    <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Country Modal */}
      <Modal visible={showCountryModal} transparent animationType="slide">
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1} 
          onPress={() => setShowCountryModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Country</Text>
            {COUNTRIES.map((country) => (
              <TouchableOpacity
                key={country}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('country', country);
                  setShowCountryModal(false);
                }}
              >
                <Text style={styles.modalItemText}>{country}</Text>
                {form.country === country && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Country Code Modal */}
      <Modal visible={showCountryCodeModal} transparent animationType="slide">
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1} 
          onPress={() => setShowCountryCodeModal(false)}
        >
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Country Code</Text>
            {COUNTRY_CODES.map((code) => (
              <TouchableOpacity
                key={code}
                style={styles.modalItem}
                onPress={() => {
                  handleChange('countryCode', code);
                  setShowCountryCodeModal(false);
                }}
              >
                <Text style={styles.modalItemText}>+{code}</Text>
                {form.countryCode === code && (
                  <SvgIcon name="checkmark" size={20} color={colors.secondary} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Custom Dialog Modal */}
      <Modal visible={dialog.visible} transparent animationType="fade">
        <View style={styles.dialogOverlay}>
          <View style={styles.dialogContainer}>
            <View style={[styles.dialogHeader, dialog.type === 'success' ? styles.dialogHeaderSuccess : styles.dialogHeaderError]}>
              <SvgIcon 
                name={dialog.type === 'success' ? 'checkmark-circle' : 'alert-circle'} 
                size={50} 
                color="#fff" 
              />
            </View>
            <View style={styles.dialogBody}>
              <Text style={styles.dialogTitle}>{dialog.title}</Text>
              <Text style={styles.dialogMessage}>{dialog.message}</Text>
              <TouchableOpacity 
                style={[styles.dialogButton, dialog.type === 'success' ? styles.dialogButtonSuccess : styles.dialogButtonError]}
                onPress={handleDialogConfirm}
              >
                <Text style={styles.dialogButtonText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      <Toast visible={toast.visible} message={toast.message} />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  headerSection: {
    backgroundColor: colors.secondary,
    padding: 16,
    paddingTop: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerTextContainer: {
    flex: 1,
  },
  headerSpacer: {
    width: 40,
  },
  container: {
    padding: 20,
    backgroundColor: '#FFFFFF',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
    textAlign: 'center',
    marginBottom: 4,
  },
  requiredNote: {
    fontSize: 12,
    color: '#fff',
    textAlign: 'center',
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.secondary,
    marginTop: 20,
    marginBottom: 16,
  },
  inputWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  floatingLabel: {
    position: 'absolute',
    top: -10,
    left: 12,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 4,
    fontSize: 12,
    fontWeight: '500',
    color: colors.secondary,
    zIndex: 1,
  },
  input: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: '#F9FAFB',
    fontSize: 14,
    color: colors.primary,
  },
  inputFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  dropdown: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    padding: 12,
    paddingTop: 14,
    backgroundColor: '#F9FAFB',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  disabledDropdown: {
    backgroundColor: '#E8E8E8',
    opacity: 0.7,
  },
  dropdownText: {
    fontSize: 14,
    color: colors.primary,
  },
  dropdownPlaceholder: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  halfWidth: {
    flex: 1,
  },
  groupedInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    backgroundColor: '#F9FAFB',
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 8,
  },
  groupedInputContainerFocused: {
    borderColor: colors.secondary,
    borderWidth: 2,
  },
  countryCodeButtonGrouped: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRightWidth: 1,
    borderRightColor: '#D1D5DB',
  },
  phoneInputGrouped: {
    flex: 1,
    fontSize: 14,
    color: colors.primary,
    paddingVertical: 8,
    outlineWidth: 0,
  },
  countryCodeText: {
    fontSize: 14,
    color: colors.primary,
    fontWeight: '500',
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 24,
    marginBottom: 20,
  },
  button: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  registerButton: {
    backgroundColor: colors.secondary,
  },
  clearButton: {
    backgroundColor: '#6c757d',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    padding: 20,
    maxHeight: '70%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  modalScroll: {
    maxHeight: 400,
  },
  modalItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalItemText: {
    fontSize: 16,
    color: colors.text,
  },
  dialogOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialogContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  dialogHeader: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogHeaderSuccess: {
    backgroundColor: colors.success,
  },
  dialogHeaderError: {
    backgroundColor: colors.error,
  },
  dialogBody: {
    padding: 24,
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  dialogMessage: {
    fontSize: 16,
    color: colors.textSecondary,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  dialogButton: {
    paddingVertical: 12,
    paddingHorizontal: 40,
    borderRadius: 8,
    minWidth: 120,
  },
  dialogButtonSuccess: {
    backgroundColor: colors.success,
  },
  dialogButtonError: {
    backgroundColor: colors.error,
  },
  dialogButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default AddProfileScreen;
